// eslint-disable-next-line react/jsx-filename-extension
import type React from 'react';
import type Luminary from '@lumen5/luminary';

import {
    type CustomRendererComponentProps,
    CustomRendererMode,
    type EnhancedToolResult,
    type ToolCall,
    type ToolDefinition,
    type ToolExecutionContext,
    type ToolResult,
} from '../types';

import { SceneResolutionError } from './scene-addressing';
import { formatZodIssues, toLLMJSONSchema } from './zodJsonSchema';

import { z } from 'zod';
import type { ToolName } from 'js/src/agent/toolNames';
import type { AppDispatch, GetRootState, RootState } from 'js/src/libs/RootState';

/**
 * Whether the schema wants an object or a list here, looking through the wrappers a field
 * collects — `.optional()`, `.nullable()`, `.default()` — since those hide the real type.
 */
function wantsStructure(field: z.ZodType | undefined): boolean {
    let current = field;
    for (let depth = 0; current && depth < 5; depth++) {
        if (current instanceof z.ZodObject || current instanceof z.ZodArray) {
            return true;
        }
        const unwrap = (current as unknown as { unwrap?: () => z.ZodType }).unwrap;
        if (typeof unwrap !== 'function') {
            return false;
        }
        current = unwrap.call(current);
    }
    return false;
}

export interface ToolConstructorArgs {
    luminary: Luminary;
    dispatch: AppDispatch;
    getState?: GetRootState;
}

/**
 * Enhanced Tool base class with conversation spawning capabilities.
 * Tools can create child conversations for complex operations.
 * Progress is shown through conversation messages, not separate progress reporting.
 */
export abstract class Tool<
    TSchema extends z.ZodType = z.ZodType,
    TConstructorArgs extends ToolConstructorArgs = ToolConstructorArgs,
> {
    protected _luminary: Luminary;
    protected _dispatch: AppDispatch;
    protected _getState: GetRootState;
    protected abstract readonly schema: TSchema;
    protected abstract readonly toolName: ToolName;
    protected abstract readonly toolDescription: string;
    // getToolDefinition() runs per request; the Zod->JSON conversion is memoized
    // per tool instance (instances are reused across turns). Only the conversion:
    // what extendParameters() injects can change over a conversation's life.
    private _cachedParameters?: ToolDefinition['function']['parameters'];
    public canRunAsync = false;
    /**
     * Defer this tool until every mutating tool in the same batch has finished.
     *
     * The model routinely emits an edit and the review of that edit in one batch. Without
     * this the review is hoisted into the leading parallel group and judges the video as it
     * was *before* the edits it was asked to check, so a defect the edit just fixed still
     * gets reported (and one it just introduced does not).
     *
     * Independent of `canRunAsync`, which still decides concurrency: a deferred tool shares
     * a tick with the other deferred tools only if it also sets `canRunAsync`, and otherwise
     * runs in series after them.
     */
    public runsAfterEdits = false;
    public expandedByDefault = false;
    public isLLMCallable = true;
    public customRenderer?: React.ComponentType<CustomRendererComponentProps> | null = null;
    public customRendererMode: CustomRendererMode = CustomRendererMode.INLINE_HIDDEN;
    /**
     * Optional component that renders this tool's status icon in the tool-call
     * header, replacing the default spinner/check/error icon. Use when a tool's
     * "done-ness" depends on state beyond its own tool result (e.g. a video
     * draft the agent keeps editing across later tool calls in the same turn).
     */
    public statusIndicator?: React.ComponentType<{ toolCall: ToolCall }> | null = null;

    public constructor({ luminary, dispatch, getState }: TConstructorArgs) {
        this._luminary = luminary;
        this._dispatch = dispatch;
        // A tool constructed without `getState` has no Redux store. Reading state
        // anyway (e.g. a luminary operation reaching into `state.translations`)
        // silently saw an empty object and crashed deep in a selector. Surface the
        // real cause — a missing `getState` — loudly in dev/test so it's caught
        // before shipping, while keeping a non-throwing fallback in production.
        this._getState =
            getState ||
            (() => {
                if (process.env.NODE_ENV !== 'production') {
                    throw new Error(
                        `${this.constructor.name} read Redux state but was constructed without a getState. ` +
                            `Pass getState when constructing this tool.`,
                    );
                }
                return {} as RootState;
            });
    }

    protected get luminary(): Luminary {
        return this._luminary;
    }

    /**
     * Repoints this tool at a different Luminary. Used to rebind edit tools to a
     * newly created/selected video draft mid-turn, without rebuilding the tool
     * list (the running executor holds these instances).
     */
    public setLuminary(luminary: Luminary): void {
        this._luminary = luminary;
    }

    protected get dispatch(): AppDispatch {
        return this._dispatch;
    }

    protected get getState(): GetRootState {
        return this._getState;
    }

    /**
     * The JSON schema + name/description the LLM sees for this tool, derived from the
     * tool's Zod `schema` (the conversion is memoized — see `_cachedParameters`).
     */
    public getToolDefinition(): ToolDefinition {
        if (!this._cachedParameters) {
            this._cachedParameters = toLLMJSONSchema(this.schema);
        }
        return {
            type: 'function',
            function: {
                name: this.toolName,
                description: this.toolDescription,
                // A JSON round-trip rather than structuredClone: this is JSON Schema, so it
                // survives one intact, and jsdom does not define structuredClone.
                parameters: this.extendParameters(
                    JSON.parse(JSON.stringify(this._cachedParameters)),
                ),
            },
        };
    }

    /**
     * Hook for schema-first tools whose model-facing parameters depend on
     * per-instance state a module-level Zod schema can't see — e.g. an `enum` of
     * the template ids available to this agent. Runs on every call, against a fresh
     * copy of the JSON derived from `schema`, so it is free to inject by mutating
     * what it is handed. Per call rather than once, because a tool instance outlives
     * the state it reads: the tools are built when the conversation opens, and the
     * video they describe picks its aspect ratio partway through. Default: identity.
     * The Zod `schema` still types and validates args; this only adjusts what the
     * model sees, so a tool using it should enforce the injected constraint in
     * `execute` too.
     */
    protected extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        return parameters;
    }

    public abstract getToolDisplayName(toolCallArgs?: string): string;

    /**
     * Material Symbols icon name shown in the running status line while this
     * tool executes (e.g. 'perm_media' for a media search). Chosen to match the
     * concept of the tool so the spinning indicator reads at a glance. Required
     * so every tool — including new ones — declares an intentional icon rather
     * than falling back to a generic default.
     */
    public abstract get statusIcon(): string;

    /**
     * Called after the tool executes successfully, with the validated args.
     * Default no-op. Editing tools override this to react to a successful edit
     * (e.g. refreshing the video-draft thumbnail when the first scene changes).
     */
    protected onEditApplied(_args: z.infer<TSchema>): void {}

    public async executeWithValidation(
        args: Record<string, unknown>,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        try {
            const preprocessedArgs = this.preprocessArgs(this.decodeStructuredArgs(args));
            const validatedArgs = this.validateArgs(preprocessedArgs);
            const result = await this.execute(validatedArgs, context);

            // If tool returns enhanced result, return it as-is
            if (typeof result === 'object' && 'result' in result) {
                const enhanced = {
                    ...result,
                    isSuccess: result.isSuccess ?? this.determineSuccess(result.result),
                };
                if (enhanced.isSuccess) {
                    this.onEditApplied(validatedArgs);
                }
                return enhanced;
            }

            // Otherwise wrap simple result in enhanced format. A tool can also
            // return an array of text/image content parts directly (typed as
            // ToolResult); flatten that to a string for EnhancedToolResult so
            // the success heuristic and downstream renderers stay string-based.
            const resultString =
                typeof result === 'string'
                    ? result
                    : result
                          .map((part) => (part.type === 'text' ? part.text : '[image]'))
                          .join('\n');
            const isSuccess = this.determineSuccess(resultString);
            if (isSuccess) {
                this.onEditApplied(validatedArgs);
            }
            return {
                result: resultString,
                isSuccess,
            };
        } catch (error) {
            // A scene couldn't be resolved from the args — the message is already written for the
            // model, so surface it directly rather than wrapping it in tool-internal context.
            if (error instanceof SceneResolutionError) {
                return { result: `Error: ${error.message}`, isSuccess: false };
            }

            // Return structured error message with more context
            const errorMessage =
                error instanceof Error
                    ? `Error in ${this.getToolDefinition().function.name}: ${error.message}${
                          error.name !== 'Error' ? ` (${error.name})` : ''
                      }`
                    : `Error in ${this.getToolDefinition().function.name}: Unknown error occurred`;

            return {
                result: errorMessage,
                isSuccess: false,
            };
        }
    }

    /**
     * Determines if a tool result indicates success based on the result string
     */
    private determineSuccess(result: string): boolean {
        if (typeof result !== 'string') return false;

        // Check if result starts with success indicators
        const successPrefixes = ['Success', 'Successfully', 'Complete', 'Completed'];
        const errorPrefixes = ['Error', 'Failed', 'Failure', 'Unable to'];

        const resultLower = result.toLowerCase();

        // Check for explicit error indicators first
        if (errorPrefixes.some((prefix) => resultLower.startsWith(prefix.toLowerCase()))) {
            return false;
        }

        // Check for explicit success indicators
        if (successPrefixes.some((prefix) => resultLower.startsWith(prefix.toLowerCase()))) {
            return true;
        }

        // If no explicit indicators, assume success (for backward compatibility)
        return true;
    }

    /**
     * Hook for subclasses to transform arguments before schema validation.
     * Override this to clamp, normalize, or adjust args before Zod validation runs.
     */
    protected preprocessArgs(args: Record<string, any>): Record<string, any> {
        return args;
    }

    /**
     * Asked for an object or a list, a model sometimes sends the JSON *text* of one. Zod rejects
     * the string, the call fails, and the turn is spent resending an argument that was already
     * right in substance — so decode it here rather than teaching every schema to accept both
     * shapes. Only fields the schema wants structured are considered, so a string that is meant
     * to be a string is never reinterpreted.
     */
    private decodeStructuredArgs(args: Record<string, unknown>): Record<string, unknown> {
        const shape = (this.schema as unknown as { shape?: Record<string, z.ZodType> }).shape;
        if (!shape) {
            return args;
        }
        const decoded = { ...args };
        for (const [key, value] of Object.entries(args)) {
            if (typeof value !== 'string' || !wantsStructure(shape[key])) {
                continue;
            }
            const text = value.trim();
            if (!text.startsWith('{') && !text.startsWith('[')) {
                continue;
            }
            try {
                decoded[key] = JSON.parse(text);
            } catch {
                // Leave it: Zod's own message about the field is more use than one about JSON.
            }
        }
        return decoded;
    }

    /**
     * Validates arguments against the tool's Zod `schema` — the same schema that
     * derives the LLM-facing JSON schema — and returns the parsed, typed args.
     */
    private validateArgs(args: Record<string, unknown>): z.infer<TSchema> {
        const result = this.schema.safeParse(args);
        if (!result.success) {
            throw new Error(`Validation failed: ${formatZodIssues(result.error)}`);
        }
        return result.data;
    }

    public abstract execute(
        args: z.infer<TSchema>,
        context: ToolExecutionContext,
    ): Promise<ToolResult | EnhancedToolResult>;
}
